import { BoundingBox, Hole, TeamRound, TeamRoundPlayerScoreMap } from "./types"
import { Point } from "./types"

/**
 * Returns the TeamRound for the given team ID, with the current user's player first in the players array.
 *
 * @param {number} currentUserId - The ID of the current user
 * @param {number} teamId - The ID of the league team
 * @param {TeamRound[]} teamRounds - The array of team rounds
 * @return {TeamRound} The team round
 */
export function findTeamRound(currentUserId: number, teamId: number | null, teamRounds: TeamRound[]): TeamRound | null {
  if (!teamId) return null
  if (!teamRounds) return null

  for (let teamRound of teamRounds) {
    if (teamRound.teamId === teamId) {
      const playerTeamRound: TeamRound = {
        id: teamRound.id,
        teamId: teamRound.teamId,
        name: teamRound.name,
        startTimeOfDay: teamRound.startTimeOfDay,
        startHoleNumber: teamRound.startHoleNumber,
        startGroup: teamRound.startGroup,
        startGroupLabel: teamRound.startGroupLabel,
        players: []
      }
      Object.assign(playerTeamRound, teamRound)
      playerTeamRound.players = []

      for (let p of teamRound.players) {
        if (p.user.id === currentUserId) {
          playerTeamRound.players.unshift(p)
        } else {
          playerTeamRound.players.push(p)
        }
      }

      return playerTeamRound
    }
  }
}

export function strokesForHole(playerHandicap: number, holeHandicap: number) {
  let strokes = 0

  if (!playerHandicap || !holeHandicap) {
    return 0
  }

  if (playerHandicap >= holeHandicap) {
    strokes++
  }

  playerHandicap = playerHandicap - 9

  if (playerHandicap >= holeHandicap) {
    strokes++
  }

  return strokes
}

export function holeMaxStrokes(par: number, strokes: number) {
  return par + strokes + 2
}

export function strokeChoiceRows(max: number): number[][] {
  const firstRowLength = max > 4 ? Math.ceil(max / 2) : max

  const firstRow = []
  for (let i = 1; i <= firstRowLength; i++) {
    firstRow.push(i)
  }

  const secondRow = []
  for (let i = firstRowLength + 1; i <= max; i++) {
    secondRow.push(i)
  }

  return [firstRow, secondRow]
}

export function getPlayerHoleStrokes(
  scores: TeamRoundPlayerScoreMap,
  teamRoundPlayerId: number,
  holeIndex: number
): number | null {
  if (scores) {
    const playerScores = scores[teamRoundPlayerId]
    if (playerScores) {
      return playerScores[holeIndex]
    }
  }

  return null
}

/**
 * Returns the holes a team plays, in the order their scores are stored.
 *
 * 9-hole league scores are always stored at indexes 0-8, whether the team plays the front or back nine. A team only
 * plays the back nine when the course actually has one: a round can be set to start on hole 10 at a 9-hole course,
 * and those players are playing holes 1-9.
 */
export function teamRoundHoles(holes: Hole[], numberOfHoles: number, startHoleNumber: number): Hole[] {
  if (numberOfHoles !== 9) return holes

  const startIndex = startHoleNumber > 9 && holes.length >= 18 ? 9 : 0
  return holes.slice(startIndex, startIndex + 9)
}

export function getInitialHoleIndex(
  startHoleNumber: number,
  numberOfHoles: number,
  playerId: number,
  scores: TeamRoundPlayerScoreMap
): number {
  let holeIndex = startHoleNumber - 1
  if (numberOfHoles === 9 && holeIndex > 8) {
    holeIndex = holeIndex - 9
  }

  for (let i = 0; i < 9; i++) {
    const strokes = getPlayerHoleStrokes(scores, playerId, holeIndex)
    if (strokes || strokes === 0) {
      if (holeIndex === (numberOfHoles === 18 ? 17 : 8)) {
        holeIndex = 0
      } else {
        holeIndex++
      }
    } else {
      break
    }
  }
  return holeIndex
}

export function formatScore(score: number) {
  if (score > 0) {
    return `+${score}`
  } else if (score === 0) {
    return "E"
  } else {
    return `${score}`
  }
}

export function thru(holesCompleted: number, complete: boolean) {
  if (complete) {
    if (!holesCompleted) {
      return "0"
    } else if (holesCompleted < 9) {
      return `${holesCompleted}`
    } else {
      return "F"
    }
  } else if (holesCompleted >= 9) {
    return "9"
  } else if (holesCompleted > 0) {
    return `${holesCompleted}`
  } else {
    return ""
  }
}

export function scoreName(score: number): string {
  if (score <= -5) return "Double Condor"
  if (score === -4) return "Condor"
  if (score === -3) return "Albatross"
  if (score === -2) return "Eagle"
  if (score === -1) return "Birdie"
  if (score === 0) return "Par"
  if (score === 1) return "Bogey"
  return "Double Bogey"
}

export function money(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export function moneyNoCents(amount: number): string {
  if (!amount && amount != 0) return ""

  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
}

export function roundTo(number: number, precision = 0) {
  const factor = Math.pow(10, precision)
  return Math.round((number + Number.EPSILON) * factor) / factor
}

const EARTH_RADIUS = 3958.8 // Radius of the earth in miles

function deg2rad(deg) {
  return deg * (Math.PI / 180)
}

export function distanceBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = deg2rad(b.lat - a.lat)
  const dLng = deg2rad(b.lng - a.lng)

  const l =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(a.lat)) * Math.cos(deg2rad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(l), Math.sqrt(1 - l))

  return EARTH_RADIUS * c
}

export function boundingBoxForPoint({ lat, lng }: Point, distance: number, precision = 3): BoundingBox {
  return {
    minLat: roundTo(lat - (distance / EARTH_RADIUS) * (180 / Math.PI), precision),
    maxLat: roundTo(lat + (distance / EARTH_RADIUS) * (180 / Math.PI), precision),
    minLng: roundTo(lng - (distance / EARTH_RADIUS / Math.cos((Math.PI * lat) / 180)) * (180 / Math.PI), precision),
    maxLng: roundTo(lng + (distance / EARTH_RADIUS / Math.cos((Math.PI * lat) / 180)) * (180 / Math.PI), precision)
  }
}

export function roundLocation(location: { lat: number; lng: number }, precision = 3) {
  return {
    lat: roundTo(location.lat, precision),
    lng: roundTo(location.lng, precision)
  }
}

export function boundingBoxForBounds(bounds: google.maps.LatLngBounds): BoundingBox {
  return {
    minLat: roundTo(bounds.getSouthWest().lat(), 3),
    maxLat: roundTo(bounds.getNorthEast().lat(), 3),
    minLng: roundTo(bounds.getSouthWest().lng(), 3),
    maxLng: roundTo(bounds.getNorthEast().lng(), 3)
  }
}

export function toggleArray(array, value) {
  if (array.includes(value)) {
    return array.filter((t) => t !== value)
  } else {
    return [...array, value]
  }
}

export function addOrRemoveParam(url: URL | string, key: string, value: string) {
  url = new URL(url.toString())
  if (value === null) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
    }
  } else {
    if (!url.searchParams.has(key)) {
      url.searchParams.append(key, value)
    }
  }
  return url
}
